import { useRef, useState } from 'react';
import { Alert, Button } from 'antd';
import { CameraOutlined, CheckCircleFilled, VideoCameraOutlined } from '@ant-design/icons';
import { uploadDocument } from '@/api/dataroom';
import { normalizeError } from '@/api/errors';
import type { CaptureSpec, Task, TaskStatus } from '@/api/types';
import { useI18n } from '@/i18n/useI18n';
import { useTaskText } from './taskText';

/**
 * CAPTURE task (OL-22): a selfie or a short face video from the device camera, sent through the
 * documents path. `capture="user"` opens the front camera on phones; the server fixes the accepted
 * media by mode and is authoritative (415). Liveness scoring runs in the flow, never here — the room
 * shows a receipt, never a score.
 */
export function CaptureTask({
  task,
  onStatus,
}: {
  task: Task;
  onStatus: (status: TaskStatus) => void;
}) {
  const { t } = useI18n();
  const text = useTaskText(task);
  const spec = (task.spec as CaptureSpec | undefined) ?? { mode: 'SELFIE' };
  const video = spec.mode === 'VIDEO';
  // The server resolves prompts on the hub's content ladder; a room's own key is the older form.
  const prompts = task.ui?.prompts ?? (spec.promptKeys ?? []).map((key) => t(key));
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [received, setReceived] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (task.status === 'ACCEPTED') {
    return <Alert type="success" showIcon message={t('capture.receiptTitle')} />;
  }

  async function handle(file: File | undefined) {
    if (!file || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await uploadDocument(task.taskId, file);
      setReceived(true);
      onStatus(res.status);
    } catch (err) {
      const kind = normalizeError(err).kind;
      setError(
        kind === 'BAD_TYPE'
          ? t('capture.rejectedBadType')
          : kind === 'TOO_LARGE'
            ? t('capture.rejectedTooLarge')
            : t('error.inline'),
      );
    } finally {
      setBusy(false);
    }
  }

  const submitted = received || task.status === 'SUBMITTED';

  return (
    <div className="dr-stack">
      <div className="dr-meta">
        {video
          ? text('intro', 'capture.videoIntro', { seconds: spec.maxSeconds ?? 15 })
          : text('intro', 'capture.selfieIntro')}
      </div>
      {video && prompts.length > 0 && (
        <ol className="dr-capture__prompts">
          {prompts.map((prompt, i) => (
            <li key={i}>{prompt}</li>
          ))}
        </ol>
      )}
      {submitted && (
        <div className="dr-receipt">
          <CheckCircleFilled className="dr-receipt__icon" aria-hidden="true" />
          <div className="dr-receipt__body">{t('capture.receiptTitle')}</div>
        </div>
      )}
      {error && <Alert type="error" showIcon message={error} />}
      <input
        ref={inputRef}
        type="file"
        accept={video ? 'video/*' : 'image/jpeg,image/png'}
        capture="user"
        hidden
        data-testid="capture-input"
        onChange={(e) => {
          void handle(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      <Button
        type="primary"
        size="large"
        block
        loading={busy}
        icon={video ? <VideoCameraOutlined /> : <CameraOutlined />}
        onClick={() => inputRef.current?.click()}
      >
        {busy
          ? t('capture.uploading')
          : submitted
            ? t('capture.retake')
            : video
              ? text('submit', 'capture.recordVideo')
              : text('submit', 'capture.takeSelfie')}
      </Button>
    </div>
  );
}
